import { getParamValue } from 'src/utils/url-data-params';

import { FILTERS_CONFIG } from 'src/models/recipe/list/filters-config';
import { type FilterConfig } from 'src/models/recipe/list/filter-types';
import {type OptionsMap, type RecipeSearchAggregationBucketOutput, type RecipeSearchBucketFiltersInput, type RecipeSearchFiltersInput} from 'src/types';

type FilterFields = Record<string, unknown>;
type FilterConfigWithValue = [FilterConfig, unknown];

export type Aggregations = { [key: string]: { [key: number]: RecipeSearchAggregationBucketOutput } };

type UrlParams = {
  data?: string[];
};

export default class FilterData {
  filterFields: FilterFields = {};

  constructor(initialFilterFields: FilterFields | null = null) {
    if (initialFilterFields !== null) {
      this.filterFields = initialFilterFields;
    }
  }

  getFilterFields(): FilterFields {
    return this.filterFields;
  }

  getActiveFilterFields(): FilterConfigWithValue[] {
    return Object.entries(this.getFilterFields()).flatMap(([key, value]) => {
      const filterConfig = FILTERS_CONFIG.find((config) => config.code === key);
      if (!filterConfig || filterConfig.isEmpty(value) || filterConfig.excludeFromReset) {
        return [];
      }
      return [[filterConfig, value]] as FilterConfigWithValue[];
    });
  }

  getFilterField(name: string): unknown {
    return this.filterFields[name] ?? null;
  }

  setFilterFields(filterFields: FilterFields): this {
    this.filterFields = filterFields;
    return this;
  }

  hasFilterFields(): boolean {
    return Object.entries(this.getFilterFields()).some(([key, value]) => {
      const filterConfig = FILTERS_CONFIG.find((config) => config.code === key);
      return filterConfig && !filterConfig.isEmpty(value);
    });
  }

  canReset(): boolean {
    return Object.entries(this.getFilterFields()).some(([key, value]) => {
      const filterConfig = FILTERS_CONFIG.find((config) => config.code === key);
      return filterConfig && !filterConfig.excludeFromReset && !filterConfig.isEmpty(value);
    });
  }

  // getPreparedFilterData
  getDataForQuery(): RecipeSearchFiltersInput {
    return this._buildQueryData((filterConfig, filterData) => filterConfig.toQuery(filterData)) as RecipeSearchFiltersInput;
  }

  // getDataForBucketQuery – used for bucket/search filters (e.g. ingredient phrase search)
  getDataForBucketQuery(): RecipeSearchBucketFiltersInput {
    return this._buildQueryData((filterConfig, filterData) =>
      typeof (filterConfig as FilterConfig & { toQueryBucket?: (d: unknown) => unknown }).toQueryBucket === 'function'
        ? (filterConfig as FilterConfig & { toQueryBucket: (d: unknown) => unknown }).toQueryBucket(filterData)
        : filterConfig.toQuery(filterData)
    ) as RecipeSearchBucketFiltersInput;
  }

  private _buildQueryData(
    getValue: (filterConfig: FilterConfig, filterData: unknown) => unknown
  ): Record<string, unknown> {
    if (!this.hasFilterFields()) {
      return {};
    }

    const preparedFilterData: Record<string, unknown> = {};

    Object.entries(this.getFilterFields()).forEach(([filterKey, filterData]) => {
      const filterConfig = FILTERS_CONFIG.find((config) => config.code === filterKey);
      if (!filterConfig) return;

      const preparedValue = getValue(filterConfig, filterData);
      if (preparedValue === null || preparedValue === undefined) return;

      preparedFilterData[filterConfig.queryField] = preparedValue;
    });

    return preparedFilterData;
  }

  // getFiltersFromUrlParams
  async fillFiltersFromUrlParams(params: UrlParams): Promise<void> {
    const filterData: FilterFields = {};

    for (const filterConfig of FILTERS_CONFIG) {
      const paramValue = getParamValue(params, filterConfig.code);
      if (paramValue === null) {
        continue;
      }

      const options: OptionsMap = await filterConfig.resolveOptions(paramValue);

      filterData[filterConfig.code] = filterConfig.fromUrl(paramValue, options);
    }

    this.setFilterFields(filterData);
  }
}

export function getAvailableFilterBuckets(aggregations: Aggregations | null | undefined): Record<string, { code: string; label: string }[]> {
  if (!aggregations) return {};

  const availableFilters: Record<string, { code: string; label: string }[]> = {};

  Object.entries(aggregations).forEach(([aggregationKey, buckets]) => {
    const filterConfig = FILTERS_CONFIG.find((config) => config.queryField === aggregationKey);
    if (!filterConfig) return;

    const filterOptions = Object.values(buckets)
      .filter((entry): entry is RecipeSearchAggregationBucketOutput & { key: string; label: string } => !!entry.label && entry.key !== null)
      .map((entry) => ({ code: entry.key, label: entry.label }));

    if (filterOptions.length > 0) {
      availableFilters[filterConfig.code] = filterOptions;
    }
  });

  return availableFilters;
}

